/** @typedef {import("../../../api/types").Order} Order */
import React, {useState} from "react";
import axios from "axios";
import {Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from "reactstrap";
import IconLabel from "../../../shared/components/IconLabel";
import {CheckIcon, ChevronRightIcon, SendIcon} from "../../../shared/components/icons";
import CopyLinkButton from "./CopyLinkButton";
import {orderPublicLink} from "../utils/orderLink";

const ORDERS_URL = "/os/api/service_orders/";

/**
 * Shown right after an order is created: the customer's link, and a chance to
 * mark the order as sent or confirmed before going back to the list. Every way
 * out (including closing it) calls `onDone`.
 *
 * @param {{order: Order, onDone: () => void}} props
 */
const OrderCreatedModal = ({order, onDone}) => {
    const [busy, setBusy] = useState(/** @type {"send"|"confirm"|null} */ (null));
    const [error, setError] = useState(false);

    const mark = async (/** @type {"send"|"confirm"} */ action) => {
        setBusy(action);
        setError(false);
        try {
            await axios.post(`${ORDERS_URL}${order.id}/${action}/`);
            onDone();
        } catch {
            setError(true);
            setBusy(null);
        }
    };

    const toggle = busy ? undefined : onDone;

    return (
        <Modal isOpen toggle={toggle} fade={false} centered size="lg">
            <ModalHeader toggle={toggle}>Ordem #{order.number} criada</ModalHeader>
            <ModalBody>
                <p>Envie este link ao cliente para ele ver a ordem de serviço.</p>
                <div className="d-flex align-items-center gap-3 rounded px-3 py-2 bg-primary bg-opacity-10">
                    <span className="flex-grow-1 text-break" data-testid="order-link">
                        {orderPublicLink(order)}
                    </span>
                    <CopyLinkButton order={order} label="Copiar" color="primary" size="sm" outline />
                </div>
                {error && (
                    <Alert color="danger" className="mt-3 mb-0">
                        Não foi possível atualizar a ordem. Tente novamente.
                    </Alert>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={onDone} disabled={!!busy}>
                    <IconLabel icon={<ChevronRightIcon />}>Manter em aberto</IconLabel>
                </Button>
                <Button color="primary" outline onClick={() => mark("send")} disabled={!!busy}>
                    {busy === "send" ? (
                        <Spinner size="sm" />
                    ) : (
                        <IconLabel icon={<SendIcon />}>Marcar como enviada</IconLabel>
                    )}
                </Button>
                <Button color="primary" onClick={() => mark("confirm")} disabled={!!busy}>
                    {busy === "confirm" ? (
                        <Spinner size="sm" />
                    ) : (
                        <IconLabel icon={<CheckIcon />}>Marcar como confirmada</IconLabel>
                    )}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default OrderCreatedModal;
